import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Divider,
  Spinner
} from '@heroui/react';
import {
  MessageCircle
} from 'lucide-react';
import Comment from './Comment';
import CommentCreator from './CommentCreator';
import { useStomp } from '../../contexts/StompContext';

const CommentSection = ({ post, initialComments = [], isExpanded = false, onToggle, onPostUpdate }) => {
  const { addMessageHandler, isConnected } = useStomp();
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedComments, setHasLoadedComments] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreComments, setHasMoreComments] = useState(true);

  // Calculate weighted score for sorting (matches backend logic)
  const calculateWeightedScore = (comment) => {
    const upvotes = comment.upvotes || 0;
    const downvotes = comment.downvotes || 0;
    const voteScore = upvotes - downvotes;

    // Time decay factor
    const createdAt = new Date(comment.created_at);
    const hoursOld = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60);
    const timeFactor = 1.0 / (1.0 + hoursOld * 0.1);

    // Combine vote score with time factor
    if (voteScore > 0) {
      return voteScore * 2 + timeFactor;
    } else {
      return timeFactor;
    }
  };

  // Sort comments by weighted score
  const sortCommentsByScore = (comments) => {
    return [...comments].sort((a, b) => calculateWeightedScore(b) - calculateWeightedScore(a));
  };

  // Initialize with sorted comments
  useEffect(() => {
    if (initialComments.length > 0) {
      setComments(sortCommentsByScore(initialComments));
    }
  }, [initialComments]);

  const commentCount = post.comment_count || comments.length;

  // Load comments when section is expanded for the first time
  useEffect(() => {
    if (isExpanded && !hasLoadedComments && commentCount > 0) {
      loadComments();
    }
  }, [isExpanded, hasLoadedComments, commentCount]);

  // STOMP real-time comment updates
  useEffect(() => {
    if (!isConnected) return;

    const postId = post._id?.['$oid'] || post._id;

    // Handle new comments
    const handleCommentUpdate = (message, topic) => {
      console.log('CommentSection: Received comment update:', message);

      if (message.notification?.comment && message.notification?.post) {
        const messagePostId = message.notification.post._id?.['$oid'] || message.notification.post._id;

        if (messagePostId === postId) {
          console.log('CommentSection: Comment update is for this post');
          // Always add new comments to the internal state, regardless of expansion
          const newComment = message.notification.comment;
          // Add the new comment to the list if it's not already there
          setComments(prev => {
            const commentExists = prev.some(c => {
              const existingId = c._id?.['$oid'] || c._id;
              const newId = newComment._id?.['$oid'] || newComment._id;
              return existingId === newId;
            });
            if (!commentExists) {
              console.log('CommentSection: Adding new comment to list (expanded:', isExpanded, ')');
              const newList = [newComment, ...prev];
              return sortCommentsByScore(newList);
            }
            return prev;
          });
          // If expanded but not loaded yet, trigger a full reload
          if (isExpanded && !hasLoadedComments) {
            console.log('CommentSection: Loading comments to show new comment');
            loadComments();
          }
        }
      } else if (message.comment_update?.comment && message.comment_update?.post) {
        // Handle timeline comment updates (from area timeline topic)
        const messagePostId = message.comment_update.post._id?.['$oid'] || message.comment_update.post._id;
        if (messagePostId === postId) {
          console.log('CommentSection: Timeline comment update for this post (expanded:', isExpanded, ')');
          const newComment = message.comment_update.comment;
          setComments(prev => {
            const commentExists = prev.some(c => {
              const existingId = c._id?.['$oid'] || c._id;
              const newId = newComment._id?.['$oid'] || newComment._id;
              return existingId === newId;
            });
            if (!commentExists) {
              console.log('CommentSection: Adding timeline comment to list (expanded:', isExpanded, ')');
              const newList = [newComment, ...prev];
              return sortCommentsByScore(newList);
            }
            return prev;
          });
        }
      }
    };

    // Handle vote updates on comments
    const handleVoteUpdate = (message, topic) => {
      console.log('CommentSection: Received vote update:', message);
      if (message.notification?.vote && message.notification?.comment && message.notification?.post) {
        const messagePostId = message.notification.post._id?.['$oid'] || message.notification.post._id;
        if (messagePostId === postId) {
          console.log('CommentSection: Vote update is for a comment on this post');
          // Update the specific comment's vote counts
          const commentId = message.notification.comment._id?.['$oid'] || message.notification.comment._id;
          updateCommentVotes(commentId, message.notification.vote);
        }
      }
    };

    // Register handlers
    const removeCommentHandler = addMessageHandler('comment', handleCommentUpdate);
    const removeVoteHandler = addMessageHandler('vote', handleVoteUpdate);

    console.log('CommentSection: Registered STOMP handlers for post', postId);

    // Cleanup
    return () => {
      removeCommentHandler();
      removeVoteHandler();
      console.log('CommentSection: Removed STOMP handlers for post', postId);
    };
  }, [isConnected, post._id, addMessageHandler]);

  const loadComments = async (page = 1, append = false) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const postId = post._id?.['$oid'] || post._id;
      const response = await fetch(`/api/v1/posts/${postId}/comments?comment_page=${page}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const newComments = data.comments || [];
        if (append) {
          setComments(prev => [...prev, ...newComments]);
        } else {
          setComments(newComments);
        }
        setHasMoreComments(newComments.length === 5); // COMMENT_PAGE = 5
        setCurrentPage(page);
        setHasLoadedComments(true);
      } else {
        console.error('Failed to load comments');
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreComments = () => {
    if (!isLoading && hasMoreComments) {
      loadComments(currentPage + 1, true);
    }
  };

  const handleCommentAdded = (newComment, updatedPost) => {
    setComments(prev => {
      const newList = [newComment, ...prev];
      return sortCommentsByScore(newList);
    });
    // Update post with fresh data from backend if provided
    if (updatedPost && onPostUpdate) {
      onPostUpdate(updatedPost);
    }
  };

  const handleCommentUpdate = (updatedComment) => {
    setComments(prev =>
      prev.map(comment =>
        (comment._id?.['$oid'] || comment._id) === (updatedComment._id?.['$oid'] || updatedComment._id)
          ? updatedComment
          : comment
      )
    );
  };

  const handleReplyAdded = (newReply) => {
    // Update comment count for replies
    if (post.comment_count !== undefined) {
      post.comment_count += 1;
    }
  };

  // Update specific comment vote counts
  const updateCommentVotes = (commentId, voteData) => {
    setComments(prev =>
      prev.map(comment => {
        if ((comment._id?.['$oid'] || comment._id) === commentId) {
          // Update vote counts based on the vote notification
          const updatedComment = { ...comment };
          // The vote notification should contain updated counts
          if (voteData.upvotes !== undefined) {
            updatedComment.upvotes = voteData.upvotes;
          }
          if (voteData.downvotes !== undefined) {
            updatedComment.downvotes = voteData.downvotes;
          }
          console.log('CommentSection: Updated comment votes', commentId, voteData);
          return updatedComment;
        }
        // Also check replies
        if (comment.replies && comment.replies.length > 0) {
          const updatedReplies = comment.replies.map(reply => {
            if ((reply._id?.['$oid'] || reply._id) === commentId) {
              const updatedReply = { ...reply };
              if (voteData.upvotes !== undefined) {
                updatedReply.upvotes = voteData.upvotes;
              }
              if (voteData.downvotes !== undefined) {
                updatedReply.downvotes = voteData.downvotes;
              }
              return updatedReply;
            }
            return reply;
          });
          if (updatedReplies !== comment.replies) {
            return { ...comment, replies: updatedReplies };
          }
        }
        return comment;
      })
    );
  };

  return (
    <>
      {/* Only render comment section when expanded */}
      {isExpanded && (
        <div className="mt-4">
          <Card className="shadow-sm rounded-nextui bg-gray-50 border-2 border-gray-200">
            <CardBody className="p-4">
              {/* Comments List */}
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Spinner size="sm" />
                  <span className="ml-2 text-sm text-gray-500">Carregando comentários...</span>
                </div>
              ) : comments.length > 0 ? (
                <div className="space-y-1 mb-4">
                  {comments.map((comment) => (
                    <Comment
                      key={comment._id?.['$oid'] || comment._id}
                      comment={comment}
                      post={post}
                      onCommentUpdate={handleCommentUpdate}
                      onReplyAdded={handleReplyAdded}
                      level={0}
                    />
                  ))}
                  {/* Load More Comments Button */}
                  {hasMoreComments && (
                    <div className="pt-4 text-center">
                      <button
                        onClick={loadMoreComments}
                        disabled={isLoading}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                      >
                        {isLoading ? 'Carregando...' : 'Ver mais comentários'}
                      </button>
                    </div>
                  )}
                </div>
              ) : hasLoadedComments ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Seja o primeiro a comentar!</p>
                </div>
              ) : null}

              {/* Comment Creator - moved to bottom */}
              <Divider className="my-4" />
              <div>
                <CommentCreator
                  post={post}
                  onCommentAdded={handleCommentAdded}
                  placeholder="Escreva um comentário..."
                />
              </div>
            </CardBody>
          </Card>
        </div>
      )}
    </>
  );
};

export default CommentSection;
